import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../../components/Header';
import api from '../../services/api';
import { showSuccess, showError } from '../../services/alert';

const AplicacaoInsumoForm = () => {
  const { id } = useParams();
  const isEditing = Boolean(id);
  const navigate = useNavigate();

  const [form, setForm] = useState({
    safraId: '',
    insumoId: '',
    talhaoId: '',
    maquinaId: '',
    quantidade: '',
    dataAplicacao: '',
    operador: '',
    observacao: ''
  });

  const [loading, setLoading] = useState(false);
  const [safras, setSafras] = useState([]);
  const [insumos, setInsumos] = useState([]);
  const [talhoes, setTalhoes] = useState([]);
  const [maquinas, setMaquinas] = useState([]);

  useEffect(() => {
    const loadSelects = async () => {
      try {
        const [resSafras, resInsumos, resTalhoes, resMaquinas] = await Promise.all([
          api.get('/safras'),
          api.get('/insumos'),
          api.get('/talhoes'),
          api.get('/maquinas'),
        ]);

        setSafras(resSafras.data);
        setInsumos(resInsumos.data);
        setTalhoes(resTalhoes.data);
        setMaquinas(resMaquinas.data);
      } catch {
        showError('Erro ao carregar listas auxiliares.');
      }
    };

    const loadAplicacao = async () => {
      if (!isEditing) return;

      setLoading(true);

      try {
        const res = await api.get(`/aplicacoesinsumo/${id}`);

        setForm({
          safraId: res.data.safraId || '',
          insumoId: res.data.insumoId || '',
          talhaoId: res.data.talhaoId || '',
          maquinaId: res.data.maquinaId || '',
          quantidade: res.data.quantidade || '',
          dataAplicacao: res.data.dataAplicacao || '',
          operador: res.data.operador || '',
          observacao: res.data.observacao || ''
        });
      } catch {
        showError('Erro ao carregar aplicação.');
      } finally {
        setLoading(false);
      }
    };

    loadSelects();
    loadAplicacao();
  }, [id, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const payload = {
      ...form,
      safraId: Number(form.safraId),
      insumoId: Number(form.insumoId),
      talhaoId: form.talhaoId ? Number(form.talhaoId) : null,
      maquinaId: form.maquinaId ? Number(form.maquinaId) : null,
      quantidade: Number(form.quantidade)
    };

    try {
      if (isEditing) {
        await api.put(`/aplicacoesinsumo/${id}`, payload);
        showSuccess('Aplicação atualizada com sucesso.');
      } else {
        await api.post('/aplicacoesinsumo', payload);
        showSuccess('Aplicação criada com sucesso.');
      }

      navigate('/aplicacoesinsumo');
    } catch (error) {
      const message =
        error.response?.data?.message ||
        'Erro ao salvar aplicação.';

      showError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mt-4">
      <Header />

      <div className="card shadow-lg border-0 rounded-3">
        <div className="card-header bg-success text-white d-flex align-items-center gap-3 rounded-top-3">
          <button
            className="btn btn-light btn-sm d-flex align-items-center gap-1"
            onClick={() => navigate('/aplicacoesinsumo')}
            disabled={loading}
          >
            <i className="bi bi-arrow-left text-success" />
            <span className="text-success fw-semibold">Voltar</span>
          </button>

          <h5 className="mb-0">
            {isEditing ? 'Editar aplicação' : 'Nova aplicação'}
          </h5>
        </div>

        <div className="card-body">
          <form onSubmit={handleSubmit} className="row g-3">

            <div className="col-md-6">
              <label className="form-label fw-semibold">Safra *</label>
              <select
                name="safraId"
                value={form.safraId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>

                {safras.map(s => (
                  <option key={s.id} value={s.id}>
                    {s.cultura} — Plantio: {new Date(s.dataPlantio).toLocaleDateString('pt-BR')}
                    {s.talhao ? ` — Talhão: ${s.talhao.nome}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Insumo *</label>
              <select
                name="insumoId"
                value={form.insumoId}
                onChange={handleChange}
                className="form-select"
                required
                disabled={loading}
              >
                <option value="">Selecione...</option>

                {insumos.map(i => (
                  <option key={i.id} value={i.id}>
                    {i.nome} {i.estoqueAtual !== undefined ? ` — Estoque: ${i.estoqueAtual}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Talhão</label>
              <select
                name="talhaoId"
                value={form.talhaoId}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {talhoes.map(t => (
                  <option key={t.id} value={t.id}>{t.nome}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Máquina</label>
              <select
                name="maquinaId"
                value={form.maquinaId}
                onChange={handleChange}
                className="form-select"
                disabled={loading}
              >
                <option value="">Selecione...</option>
                {maquinas.map(m => (
                  <option key={m.id} value={m.id}>{m.descricao}</option>
                ))}
              </select>
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Quantidade *</label>
              <input
                type="number"
                name="quantidade"
                value={form.quantidade}
                onChange={handleChange}
                className="form-control"
                min="0.01"
                step="0.01"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Data da aplicação *</label>
              <input
                type="date"
                name="dataAplicacao"
                value={form.dataAplicacao?.substring(0, 10) || ''}
                onChange={handleChange}
                className="form-control"
                required
                disabled={loading}
              />
            </div>

            <div className="col-md-6">
              <label className="form-label fw-semibold">Operador</label>
              <input
                type="text"
                name="operador"
                value={form.operador}
                onChange={handleChange}
                className="form-control"
                disabled={loading}
              />
            </div>

            <div className="col-md-12">
              <label className="form-label fw-semibold">Observação</label>
              <textarea
                name="observacao"
                value={form.observacao}
                onChange={handleChange}
                className="form-control"
                rows="3"
                placeholder="Informe alguma observação, se necessário"
                disabled={loading}
              />
            </div>

            <div className="col-12 d-flex justify-content-end mt-3">
              <button
                type="submit"
                className="btn btn-success me-2 d-flex align-items-center gap-1"
                disabled={loading}
              >
                <i className="bi bi-save"></i>
                <span>{loading ? 'Salvando...' : 'Salvar'}</span>
              </button>

              <button
                type="button"
                className="btn btn-secondary d-flex align-items-center gap-1"
                onClick={() => navigate('/aplicacoesinsumo')}
                disabled={loading}
              >
                <i className="bi bi-x-circle"></i>
                <span>Cancelar</span>
              </button>
            </div>

          </form>
        </div>
      </div>
    </div>
  );
};

export default AplicacaoInsumoForm;
